import { useState } from 'react';
import { mediaAbs } from '@/lib/hosts';
import { qrSvg } from '@/lib/qrSvg';
import {
  drawRecord, entrantsCsv, entryUrl, entryWindow, fullName, phoneLine, prizesOf, redrawLine, signupSummary,
  useEntrants, useMyDrawings, whenLine,
  type MyDrawing, type Prize, type Winner,
} from '@/lib/drawings';

/**
 * YOUR DRAWING (PRO-DRAW-1; Peter, 2026-10-14, decision 8d40b94e). The QR on the pro's door opens their drawing's
 * sign-up page; this page is where the pro sees it working: the prize, how many people signed up, who they are
 * (name, email and phone, for the people told up front), and their own link and QR to share.
 *
 * PRO-DRAW-5 (Peter, 2026-10-18, decisions 169a061c + b1d052cb): one card per drawing — a multi-prize drawing's
 * per-prize child drawings are folded in as its prizes — and under every prize, the WINNER in full: name, tap-to-
 * email, tap-to-call, and the draw's record (entry number, when, on which TV, how many in the hat, any redraw with
 * its reason). The pro hands over the prize on this.
 *
 * Read only, live (re-read every 30 seconds). The reads and every rule behind them are lib/drawings.ts and the
 * two rpcs it names. Nothing here sends anything to anyone.
 */
export default function Drawings() {
  const { data, isLoading, error, featureLoading, featureOn } = useMyDrawings();

  if (!featureLoading && !featureOn) {
    return <div className="card text-sm text-image-charcoal">Your drawing isn&rsquo;t switched on for your studio yet.</div>;
  }
  if (featureLoading || isLoading) return <div className="card text-sm text-image-charcoal">Reading your drawing&hellip;</div>;
  if (error || !data) return <div className="card text-sm text-image-charcoal">Couldn&rsquo;t read your drawing right now. Try again in a minute.</div>;
  if (data.length === 0) return <div className="card text-sm text-image-charcoal">You don&rsquo;t have a drawing at this studio.</div>;

  return (
    <div className="space-y-6" data-testid="drawings">
      {data.map((d) => <DrawingCard key={d.id} d={d} />)}
    </div>
  );
}

function DrawingCard({ d }: { d: MyDrawing }) {
  return (
    <section className="space-y-4" data-testid={`drawing-${d.slug}`}>
      <div>
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-charcoal uppercase">{d.sponsor ?? 'Your drawing'}</div>
        <h1 className="text-2xl font-bold text-image-black">{d.name}</h1>
        <p className="text-sm text-image-charcoal mt-1">{entryWindow(d)}</p>
      </div>

      <div className="card">
        <div className="flex gap-4 items-start">
          {d.prize_image && (
            <img src={mediaAbs(d.prize_image)} alt="" className="size-24 rounded-xl object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-4xl font-extrabold text-image-black leading-none" data-testid="drawing-count">{d.entries}</div>
            <div className="text-sm font-semibold text-image-black mt-1">{d.entries === 1 ? 'person signed up' : 'people signed up'}</div>
            <p className="text-xs text-image-charcoal mt-2">{signupSummary(d)}</p>
          </div>
        </div>
        <PrizeList d={d} />
      </div>

      <WhoSignedUp d={d} />
      <ShareLink slug={d.slug} />
    </section>
  );
}

/** Every prize, numbered, with its winner in full or "Not drawn yet." (PRO-DRAW-5). */
function PrizeList({ d }: { d: MyDrawing }) {
  const prizes = prizesOf(d);
  return (
    <div className="mt-4 text-sm text-image-black" data-testid="drawing-prizes">
      <div className="font-semibold">{prizes.length === 1 ? 'The prize' : 'The prizes'}</div>
      <ol className="mt-2 space-y-3">
        {prizes.map((p) => <PrizeRow key={`${p.giveaway_id}-${p.no}`} p={p} many={prizes.length > 1} />)}
      </ol>
    </div>
  );
}

function PrizeRow({ p, many }: { p: Prize; many: boolean }) {
  return (
    <li className="flex gap-3 items-start" data-testid="drawing-prize">
      {many && (
        <span className="shrink-0 size-6 rounded-full bg-image-light-gray/60 text-[11px] font-bold grid place-items-center" aria-hidden="true">{p.no}</span>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{p.prize}</div>
        {p.winner ? <WinnerBlock w={p.winner} /> : <div className="text-xs text-image-charcoal mt-0.5">Not drawn yet.</div>}
        {p.redraws?.map((r, i) => (
          <div key={`${r.drawn_at}-${i}`} className="text-[11px] text-image-charcoal mt-1" data-testid="prize-redraw">{redrawLine(r)}</div>
        ))}
      </div>
    </li>
  );
}

/** The person who is owed the prize: whole name, tap-to-email, tap-to-call, and the record of the draw. */
function WinnerBlock({ w }: { w: Winner }) {
  return (
    <div className="mt-1 rounded-lg bg-image-light-gray/40 px-3 py-2" data-testid="prize-winner">
      <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-charcoal uppercase">Winner</div>
      <div className="font-semibold text-image-black">{fullName(w) || 'No name given'}</div>
      {w.email && <a href={`mailto:${w.email}`} className="text-xs text-image-charcoal underline break-all block">{w.email}</a>}
      {w.phone && <a href={`tel:${w.phone}`} className="text-xs text-image-charcoal underline block" data-testid="winner-phone">{phoneLine(w.phone)}</a>}
      <div className="text-[11px] text-image-charcoal mt-1">{drawRecord(w)}</div>
    </div>
  );
}

function WhoSignedUp({ d }: { d: MyDrawing }) {
  const { data, isLoading, error } = useEntrants(d.id);
  const download = () => {
    if (!data?.length) return;
    const blob = new Blob([entrantsCsv(data)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${d.slug}-sign-ups.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="card" data-testid="drawing-entrants">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-image-black">Who signed up</h2>
        {!!data?.length && (
          <button type="button" className="text-sm font-semibold text-image-pink underline" onClick={download}>
            Download the list
          </button>
        )}
      </div>
      <p className="text-xs text-image-charcoal mt-1">
        Everyone here agreed that you receive their contact details, so you can follow up with them. This list updates by itself.
      </p>
      {isLoading ? (
        <p className="text-sm text-image-charcoal mt-3">Reading the list&hellip;</p>
      ) : error ? (
        <p className="text-sm text-image-charcoal mt-3">Couldn&rsquo;t read the list right now. Try again in a minute.</p>
      ) : !data?.length ? (
        <p className="text-sm text-image-charcoal mt-3">No names to show yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-image-light-gray/60">
          {data.map((e, i) => (
            <li key={`${e.email}-${i}`} className="py-2 flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-image-black truncate">{`${e.first_name} ${e.last_name}`.trim() || 'No name given'}</div>
                <a href={`mailto:${e.email}`} className="text-xs text-image-charcoal underline break-all block">{e.email}</a>
                {e.phone && (
                  <a href={`tel:${e.phone}`} className="text-xs text-image-charcoal underline block" data-testid="entrant-phone">{phoneLine(e.phone)}</a>
                )}
              </div>
              <div className="text-[11px] text-image-charcoal shrink-0">{whenLine(e.entered_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ShareLink({ slug }: { slug: string }) {
  const url = entryUrl(slug);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="card" data-testid="drawing-share">
      <h2 className="text-lg font-bold text-image-black">Get more sign-ups</h2>
      <p className="text-xs text-image-charcoal mt-1">
        This is the same sign-up page as the QR on your door. Post the link on Instagram or send it to your clients.
      </p>
      <div className="flex gap-4 items-center mt-3">
        <div className="shrink-0 bg-white rounded-lg p-1" aria-hidden="true" dangerouslySetInnerHTML={{ __html: qrSvg(url, 112) }} />
        <div className="min-w-0 space-y-2">
          <div className="text-xs text-image-black break-all">{url}</div>
          <button type="button" className="btn-primary text-sm" onClick={() => { void copy(); }}>
            {copied ? 'Copied' : 'Copy the link'}
          </button>
        </div>
      </div>
    </div>
  );
}
